import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Smartphone, QrCode, TrendingUp } from 'lucide-react';

const WHATSAPP_LINK = 'https://wa.me/523312949249?text=Quiero%20mi%20menú%20digital%20para%20mi%20negocio';

// Animation constants
const fadeInUpAnimation = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: 'easeOut' },
};

const badgeAnimation = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { delay: 0.2, duration: 0.5 },
};

const imageAnimation = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.8, delay: 0.3 },
};

const socialProofAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.6, duration: 0.5 },
};

const floatingAnimation = {
  animate: { y: [0, -10, 0] },
  transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
};

const floatingSlowAnimation = {
  animate: { y: [0, 10, 0] },
  transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
};

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-20" />
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-[hsl(var(--cta-orange))]/10 rounded-full blur-3xl opacity-20" />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            {...fadeInUpAnimation}
            className="text-left space-y-8"
          >
            <motion.div
              {...badgeAnimation}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-primary/20"
            >
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-sm font-medium text-primary">Sistema de Menú Inteligente</span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
              <span className="text-foreground">Tu menú te está </span>
              <span className="text-gradient block mt-2">haciendo perder dinero...</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              Conviértelo en una experiencia que hace que tus clientes pidan más. Diseñamos menús digitales inteligentes que guían la decisión del cliente, reducen la indecisión y aumentan el ticket promedio desde el primer día.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[hsl(var(--cta-orange))] to-[hsl(var(--cta-orange-hover))] text-white hover:shadow-[0_0_30px_hsl(var(--cta-orange)/0.4)] transition-all duration-300 text-base sm:text-lg px-8 py-6 rounded-xl"
                onClick={() => window.open(WHATSAPP_LINK, '_blank')}
              >
                Quiero mi menú que venda
              </Button>
            </div>

            <motion.div
              {...socialProofAnimation}
              className="flex flex-wrap items-center gap-6 pt-4"
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground">Aumenta el ticket promedio</span>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground">Experiencia tipo app</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            {...imageAnimation}
            className="relative"
          >
            <div className="relative z-10">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_digital-menu-launch/artifacts/rvihagng_IMAGEN%201.png"
                  alt="Digital menu experience"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>
              
              <motion.div
                {...floatingAnimation}
                className="absolute -top-6 -right-6 glass-card p-4 rounded-2xl border border-primary/30"
              >
                <QrCode className="w-8 h-8 text-primary" />
              </motion.div>
              
              <motion.div
                {...floatingSlowAnimation}
                className="absolute -bottom-6 -left-6 glass-card p-6 rounded-2xl border border-[hsl(var(--cta-orange))]/30"
              >
                <div className="text-sm text-muted-foreground mb-1">Ticket Promedio</div>
                <div className="text-2xl font-bold text-[hsl(var(--cta-orange))]">+35%</div>
              </motion.div>
            </div>
            
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/5 rounded-full blur-3xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
